'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { MapPin, Phone, Mail, Globe, Building2, Users, Trophy, Award, ChevronDown, ArrowRight, Star, CheckCircle2 } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

interface CollegeDetailProps {
  college: {
    slug: string;
    name: string;
    shortName: string;
    city: string;
    state: string;
    type: string;
    nirf: number | null;
    naac: string;
    mbbsSeats: number;
    annualFees: number;
    established: number;
    cutoffAIQ: string;
    hostel: boolean;
    affiliated: string;
    phone: string;
    email: string;
    website: string;
    description: string;
    tags: string[];
    cutoffHistory: { year: number; opening: number; closing: number; category: string }[];
    facilities: string[];
    faqs: { q: string; a: string }[];
  };
}

const formatFees = (fees: number) => {
  if (fees >= 100000) return `₹${(fees / 100000).toFixed(1)}L/yr`;
  return `₹${fees.toLocaleString('en-IN')}/yr`;
};

export default function CollegeDetailClient({ college }: CollegeDetailProps) {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  return (
    <div className="pt-20 pb-16">
      {/* Hero Banner */}
      <div className="relative bg-gradient-to-br from-primary/8 via-transparent to-accent/6 border-b border-slate-100 py-12 px-6 lg:px-8">
        <div className="max-w-screen-xl mx-auto">
          <div className="flex flex-col lg:flex-row items-start lg:items-center gap-6">
            <div className="w-20 h-20 rounded-3xl bg-gradient-to-br from-primary/15 to-accent/15 flex items-center justify-center flex-shrink-0 border border-primary/10">
              <Building2 size={36} className="text-primary" />
            </div>
            <div className="flex-1">
              <div className="flex flex-wrap gap-2 mb-3">
                {college.tags.map(tag => (
                  <span key={tag} className="text-xs px-2.5 py-1 bg-primary/8 text-primary border border-primary/15 rounded-full font-semibold">{tag}</span>
                ))}
              </div>
              <h1 className="text-3xl sm:text-4xl font-extrabold text-foreground mb-2 tracking-tight">{college.name}</h1>
              <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
                <span className="flex items-center gap-1.5"><MapPin size={14} />{college.city}, {college.state}</span>
                <span className="flex items-center gap-1.5"><Building2 size={14} />{college.type}</span>
                <span className="flex items-center gap-1.5"><Star size={14} />Est. {college.established}</span>
              </div>
            </div>
            <div className="flex gap-3 flex-shrink-0">
              <Link href={`/neet-college-predictor`} className="btn-primary flex items-center gap-2 text-sm px-5 py-3">
                <ArrowRight size={15} />
                Predict My Chances
              </Link>
              <Link href="/student-dashboard" className="btn-secondary flex items-center gap-2 text-sm px-5 py-3">
                Book Counselling
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-screen-xl mx-auto px-6 lg:px-8 py-10">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Overview */}
            <div className="glass-card-white rounded-3xl p-6 border border-white/60">
              <h2 className="text-xl font-bold text-foreground mb-4">Overview</h2>
              <p className="text-muted-foreground leading-relaxed">{college.description}</p>
            </div>

            {/* Key Stats */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {[
                { label: 'MBBS Seats', value: college.mbbsSeats, icon: Users, color: 'text-primary', bg: 'bg-primary/8' },
                { label: 'Annual Fees', value: formatFees(college.annualFees), icon: Trophy, color: 'text-success', bg: 'bg-success/8' },
                { label: 'NIRF Rank', value: college.nirf ? `#${college.nirf}` : 'N/A', icon: Award, color: 'text-warning', bg: 'bg-warning/8' },
                { label: 'NAAC Grade', value: college.naac, icon: Star, color: 'text-accent', bg: 'bg-accent/8' },
              ].map(stat => (
                <div key={stat.label} className="glass-card-white rounded-2xl p-4 border border-white/60 text-center">
                  <div className={`w-10 h-10 rounded-xl ${stat.bg} flex items-center justify-center mx-auto mb-2`}>
                    <stat.icon size={18} className={stat.color} />
                  </div>
                  <p className="text-xl font-extrabold text-foreground">{stat.value}</p>
                  <p className="text-xs text-muted-foreground mt-1">{stat.label}</p>
                </div>
              ))}
            </div>

            {/* Cutoff History Chart */}
            <div className="glass-card-white rounded-3xl p-6 border border-white/60">
              <h2 className="text-xl font-bold text-foreground mb-2">Cutoff History (General · AIQ)</h2>
              <p className="text-xs text-muted-foreground mb-5">Closing rank trend over past 4 years</p>
              <ResponsiveContainer width="100%" height={220}>
                <LineChart data={college.cutoffHistory}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(15,23,42,0.06)" />
                  <XAxis dataKey="year" tick={{ fontSize: 12, fill: '#64748b' }} />
                  <YAxis tick={{ fontSize: 12, fill: '#64748b' }} />
                  <Tooltip contentStyle={{ background: 'rgba(255,255,255,0.95)', border: '1px solid rgba(15,23,42,0.1)', borderRadius: '12px', fontSize: '12px' }} />
                  <Line type="monotone" dataKey="closing" stroke="#7c3aed" strokeWidth={2.5} dot={{ r: 5, fill: '#7c3aed' }} name="Closing Rank" />
                  <Line type="monotone" dataKey="opening" stroke="#0891b2" strokeWidth={2} strokeDasharray="5 5" dot={{ r: 4, fill: '#0891b2' }} name="Opening Rank" />
                </LineChart>
              </ResponsiveContainer>
            </div>

            {/* Facilities */}
            <div className="glass-card-white rounded-3xl p-6 border border-white/60">
              <h2 className="text-xl font-bold text-foreground mb-4">Facilities & Infrastructure</h2>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {college.facilities.map(f => (
                  <div key={f} className="flex items-center gap-2 p-3 bg-slate-50 rounded-xl">
                    <CheckCircle2 size={14} className="text-success flex-shrink-0" />
                    <span className="text-sm text-foreground font-medium">{f}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* FAQ */}
            <div className="glass-card-white rounded-3xl p-6 border border-white/60">
              <h2 className="text-xl font-bold text-foreground mb-4">Frequently Asked Questions</h2>
              <div className="space-y-3">
                {college.faqs.map((faq, i) => (
                  <div key={i} className="border border-slate-100 rounded-2xl overflow-hidden">
                    <button
                      onClick={() => setOpenFaq(openFaq === i ? null : i)}
                      className="w-full flex items-center justify-between p-4 text-left hover:bg-slate-50 transition-colors"
                    >
                      <span className="text-sm font-semibold text-foreground pr-4">{faq.q}</span>
                      <ChevronDown size={16} className={`text-muted-foreground flex-shrink-0 transition-transform ${openFaq === i ? 'rotate-180' : ''}`} />
                    </button>
                    {openFaq === i && (
                      <div className="px-4 pb-4">
                        <p className="text-sm text-muted-foreground leading-relaxed">{faq.a}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-5">
            {/* Quick Info */}
            <div className="glass-card-white rounded-3xl p-5 border border-white/60">
              <h3 className="text-base font-bold text-foreground mb-4">Quick Information</h3>
              <div className="space-y-3">
                {[
                  { label: 'AIQ Cutoff', value: college.cutoffAIQ },
                  { label: 'Affiliated To', value: college.affiliated },
                  { label: 'Hostel', value: college.hostel ? 'Available' : 'Not Available' },
                  { label: 'Established', value: college.established.toString() },
                ].map(item => (
                  <div key={item.label} className="flex items-start justify-between gap-2">
                    <span className="text-xs text-muted-foreground">{item.label}</span>
                    <span className="text-xs font-semibold text-foreground text-right">{item.value}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Contact */}
            <div className="glass-card-white rounded-3xl p-5 border border-white/60">
              <h3 className="text-base font-bold text-foreground mb-4">Contact</h3>
              <div className="space-y-3">
                <a href={`tel:${college.phone}`} className="flex items-center gap-2.5 text-sm text-muted-foreground hover:text-foreground transition-colors">
                  <Phone size={14} className="text-primary" />
                  {college.phone}
                </a>
                <a href={`mailto:${college.email}`} className="flex items-center gap-2.5 text-sm text-muted-foreground hover:text-foreground transition-colors">
                  <Mail size={14} className="text-primary" />
                  {college.email}
                </a>
                <a href={`https://${college.website}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2.5 text-sm text-muted-foreground hover:text-foreground transition-colors">
                  <Globe size={14} className="text-primary" />
                  {college.website}
                </a>
              </div>
            </div>

            {/* CTAs */}
            <div className="glass-card-white rounded-3xl p-5 border border-white/60 space-y-3">
              <Link href="/neet-college-predictor" className="btn-primary w-full justify-center text-sm py-3 gap-2">
                <ArrowRight size={15} />
                Predict My Chances
              </Link>
              <Link href="/student-dashboard" className="btn-secondary w-full justify-center text-sm py-3">
                Book Expert Counselling
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
